import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import BasicInput from './BasicInput';
import useCountdown from '../hooks/useCountdown';
import emailSentImg from '../assets/email_sent.svg';

const EmailVerification = ({
  email,
  verificationCode,
  setVerificationCode,
  onVerify,
  onResend,
  errors,
  isLoading,
  contactPath = '/contact',
  showEmailBadge = true,
  startCountdownOnMount = true
}) => {
  const { t } = useTranslation();
  const { timeLeft, isActive, start } = useCountdown(60);

  // Start countdown when component mounts (when verification code is first sent)
  useEffect(() => {
    if (startCountdownOnMount) {
      start();
    }
  }, [startCountdownOnMount, start]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onVerify();
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(numericValue);
  };

  const handleResend = async () => {
    await onResend();
    start(); // Start the countdown after resending
  };

  return (
    <div className="max-w-[406px] mx-auto flex flex-col items-center gap-9">
      <div className="relative w-[280px] h-[183px]">
        <img 
          src={emailSentImg} 
          alt="Email sent" 
          className="w-full h-full"
        />
        {showEmailBadge && (
          <div className="absolute top-[26px] left-[164px] w-8 h-8 bg-[#ff5501] rounded-full border-2 border-white flex items-center justify-center">
            <span className="text-white text-2xl font-black">1</span>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2">
          <h2 className="text-lg font-bold text-[#111111]">
            {t('auth.signup.verification.title')}
          </h2>
          <p className="text-base text-[#666666]">
            {t('auth.signup.verification.code_sent', { email })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <BasicInput
              title={t('auth.signup.verification.code_label')}
              type="text"
              placeholder={t('auth.signup.verification.code_placeholder')}
              value={verificationCode}
              onChange={handleVerificationCodeChange}
              error={errors?.verificationCode}
              required
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
            />
            <div className="flex items-center gap-1 pt-0.5">
              {isActive ? (
                <span className="text-xs text-[#aaaaaa]">
                  {t('auth.signup.verification.no_code_countdown', { seconds: timeLeft })}
                </span>
              ) : (
                <>
                  <span className="text-xs text-[#aaaaaa]">
                    {t('auth.signup.verification.no_code')}
                  </span>
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-xs text-[#2966ff]"
                  >
                    {t('auth.signup.verification.resend')}
                  </button>
                </>
              )}
            </div>
          </div>

          <Button
            variant="primary"
            size="xl"
            className="w-full"
            type="submit"
            disabled={!verificationCode || verificationCode.length !== 6 || isLoading}
          >
            {isLoading ? t('common.loading') : t('auth.signup.verification.next')}
          </Button>
        </form>

        <div className="w-full p-6 bg-[#f7f7f7] rounded-md">
          <h3 className="text-xs font-bold text-[#333333] mb-3">
            {t('auth.signup.verification.help_title')}
          </h3>
          <p className="text-xs text-[#999999]">
            {t('auth.signup.verification.help_text')}
            <Link to={contactPath} className="text-[#2966ff] underline">
              {t('auth.signup.verification.contact_us')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 